import { useEffect, useMemo, useState } from "react";
import Fuse from "fuse.js";

import muni from "./data/muni_saguache_mtc.json";
import county from "./data/county_saguache_mtc.json";
import state from "./data/state_crs.json";

import { getCourtDates2026 } from "./courts";

const JURIS = [
  { key: "MUNI_SAGUACHE", label: "Saguache Municipal" },
  { key: "COUNTY_SAGUACHE", label: "Saguache County" },
  { key: "STATE_CO", label: "Colorado State (CRS)" },
];

const ROLES = ["Deputy", "Supervisor", "Admin"];

const PASS_ON_TYPES = [
  "Officer Safety",
  "Shift Recap",
  "ATL / BOLO",
  "Extra Patrol Request",
  "Case Follow-Up",
  "Problem Property / Trespass",
  "Civil Issue",
  "Agency / Admin Notice",
  "Traffic / Roadway Issue",
  "Other",
];

const PASS_ON_PRIORITIES = ["Low", "Normal", "High"];

const ALLOWED = new Set(JURIS.map((x) => x.key));
const VACATION_CHECKS_STORAGE_KEY = "vacation_house_checks";
const RED_TAGGED_STORAGE_KEY = "red_tagged_vehicles";
const PASS_ONS_STORAGE_KEY = "pass_ons";
const USERS_STORAGE_KEY = "patrol_ops_users";
const SESSION_STORAGE_KEY = "patrol_ops_current_user";
const MODULE_SEEN_STORAGE_KEY = "patrol_ops_module_seen";

// -------------------------
// Shared helpers
// -------------------------
function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtMoney0(v) {
  const n = safeNum(v);
  return n == null ? "—" : `$${n.toFixed(0)}`;
}

function safeStr(v, fallback = "") {
  if (v == null) return fallback;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map((x) => safeStr(x, "")).filter(Boolean).join(", ");
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(`${dateString}T00:00:00`);
  return date.toLocaleDateString();
}

function formatDateTime(dateString) {
  if (!dateString) return "";
  const dt = new Date(dateString);
  return Number.isNaN(dt.getTime()) ? "" : dt.toLocaleString();
}

function canDeleteRecords(currentUser) {
  const role = currentUser?.role;
  return role === "Supervisor" || role === "Admin";
}

function canEditRecords(currentUser) {
  const role = currentUser?.role;
  return role === "Supervisor" || role === "Admin";
}

function isAdmin(currentUser) {
  return currentUser?.role === "Admin";
}

function sortByDateDesc(valueA, valueB) {
  return new Date(valueB || 0) - new Date(valueA || 0);
}

// -------------------------
// Module seen / alert helpers
// -------------------------
function getModuleSeenMap() {
  try {
    const raw = localStorage.getItem(MODULE_SEEN_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveModuleSeenMap(map) {
  localStorage.setItem(MODULE_SEEN_STORAGE_KEY, JSON.stringify(map));
}

function getUserModuleSeen(userId) {
  const map = getModuleSeenMap();
  return map[userId] || {};
}

function markModuleSeen(userId, moduleName) {
  if (!userId || !moduleName) return;
  const map = getModuleSeenMap();
  const userMap = map[userId] || {};
  userMap[moduleName] = new Date().toISOString();
  map[userId] = userMap;
  saveModuleSeenMap(map);
}

function getModuleItemTimestamp(item) {
  return item?.updatedAt || item?.createdAt || null;
}

function getUnreadModuleCount(userId, moduleName, items) {
  if (!userId) return 0;

  const userSeen = getUserModuleSeen(userId);
  const lastSeen = userSeen[moduleName];

  if (!lastSeen) return Array.isArray(items) ? items.length : 0;

  const lastSeenTime = new Date(lastSeen).getTime();

  return (Array.isArray(items) ? items : []).filter((item) => {
    const ts = getModuleItemTimestamp(item);
    if (!ts) return false;
    const itemTime = new Date(ts).getTime();
    return Number.isFinite(itemTime) && itemTime > lastSeenTime;
  }).length;
}

// -------------------------
// Local auth helpers
// -------------------------
function getStoredUsers() {
  try {
    const raw = localStorage.getItem(USERS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveStoredUsers(users) {
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
}

function getCurrentUser() {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveCurrentUser(user) {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(user));
}

function clearCurrentUser() {
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

function normalizeUsername(value) {
  return safeStr(value, "").trim().toLowerCase();
}

function syncSessionWithStoredUsers(currentSessionUser) {
  if (!currentSessionUser) return null;
  const users = getStoredUsers();
  const fresh = users.find((u) => u.id === currentSessionUser.id);
  if (!fresh || fresh.isDisabled) {
    clearCurrentUser();
    return null;
  }

  const updatedSession = {
    id: fresh.id,
    username: fresh.username,
    displayName: fresh.displayName,
    role: fresh.role || "Deputy",
  };

  saveCurrentUser(updatedSession);
  return updatedSession;
}

// -------------------------
// Court date formatting
// -------------------------
const COURT_FMT = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric",
});

function parseIsoDateOnly(dateStr) {
  if (typeof dateStr !== "string") return null;
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatTimePretty(t) {
  if (typeof t !== "string") return "";
  const s = t.trim().toLowerCase();
  if (!s) return "";

  let ss = s;
  const m1 = ss.match(/^(\d{1,2})(\d{2})(a|p)$/);
  if (m1) ss = `${m1[1]}:${m1[2]}${m1[3]}`;

  const m = ss.match(/^(\d{1,2}):(\d{2})(a|p)?$/);
  if (!m) return t;

  let hh = Number(m[1]);
  const mm = m[2];
  const ap = m[3];

  if (ap) {
    if (hh === 0) hh = 12;
    if (hh > 12) hh = hh % 12;
    return `${hh}:${mm} ${ap.toUpperCase()}M`.replace("AMM", "AM").replace("PMM", "PM");
  }

  return `${hh}:${mm}`;
}

function normalizeCourtDates(raw) {
  const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];

  const items = arr
    .map((x) => {
      if (x && typeof x === "object" && !Array.isArray(x) && !(x instanceof Date)) {
        const court = typeof x.court === "string" ? x.court : "";
        const dt = parseIsoDateOnly(x.date);
        const dateLabel = dt ? COURT_FMT.format(dt) : typeof x.date === "string" ? x.date : "";
        const timeLabel = formatTimePretty(x.time);

        const label = [dateLabel, timeLabel ? `• ${timeLabel}` : "", court ? `— ${court}` : ""]
          .filter(Boolean)
          .join(" ");

        const sort = dt ? dt.getTime() : Number.POSITIVE_INFINITY;
        return label ? { sort, label } : null;
      }

      if (x instanceof Date && !Number.isNaN(x.getTime())) {
        return { sort: x.getTime(), label: COURT_FMT.format(x) };
      }

      if (typeof x === "string") {
        const dt = parseIsoDateOnly(x) || new Date(x);
        if (dt && !Number.isNaN(dt.getTime())) {
          return { sort: dt.getTime(), label: COURT_FMT.format(dt) };
        }
        return x ? { sort: Number.POSITIVE_INFINITY, label: x } : null;
      }

      const label = String(x ?? "");
      return label ? { sort: Number.POSITIVE_INFINITY, label } : null;
    })
    .filter(Boolean);

  const seen = new Set();
  const deduped = [];
  for (const it of items) {
    if (!seen.has(it.label)) {
      seen.add(it.label);
      deduped.push(it);
    }
  }

  deduped.sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label));
  return deduped.map((x) => x.label);
}

// -------------------------
// Search logic
// -------------------------
function strictFilter(dataset, q) {
  const needle = q.trim().toLowerCase();
  if (!needle) return [];

  return dataset.filter((item) => {
    const citation = safeStr(item?.citation, "").toLowerCase();
    const code = safeStr(item?.commonCode, "").toLowerCase();
    const desc = safeStr(item?.description, "").toLowerCase();
    return citation.includes(needle) || code.includes(needle) || desc.includes(needle);
  });
}

// -------------------------
// Speeding logic
// -------------------------
function looksLikeSpeeding(row) {
  const citation = safeStr(row?.citation, "").toUpperCase();
  const desc = safeStr(row?.description, "").toLowerCase();

  return (
    row?.isSpeeding === true ||
    citation.includes("1101") ||
    citation.includes("42-4-1101") ||
    desc.includes("speeding") ||
    desc.includes("over posted limit")
  );
}

function inferRangeFromRow(row) {
  const desc = safeStr(row?.description, "");
  const citation = safeStr(row?.citation, "");

  if (safeNum(row?.mphMin) != null || row?.mphMax === null || safeNum(row?.mphMax) != null) {
    return {
      min: safeNum(row?.mphMin),
      max: row?.mphMax == null ? null : safeNum(row?.mphMax),
    };
  }

  let m = desc.match(/(\d+)\s*-\s*(\d+)/);
  if (m) {
    return { min: Number(m[1]), max: Number(m[2]) };
  }

  m = desc.match(/(\d+)\s*\+/);
  if (m) {
    return { min: Number(m[1]), max: null };
  }

  if (citation.includes("1101(1)")) return { min: 1, max: 4 };
  if (citation.includes("1101(2)")) return { min: 5, max: 9 };
  if (citation.includes("1101(3)")) return { min: 10, max: 19 };
  if (citation.includes("1101(4)")) return { min: 20, max: 24 };
  if (citation.includes("1101(5)")) return { min: 25, max: 39 };
  if (citation.includes("1101(6)")) return { min: 40, max: null };

  return null;
}

function pickSpeedingBracket(dataset, mphOver) {
  if (mphOver == null || Number.isNaN(mphOver) || mphOver < 1) return null;

  const candidates = dataset.filter(looksLikeSpeeding);

  for (const row of candidates) {
    const range = inferRangeFromRow(row);
    if (!range) continue;

    const meetsMin = range.min == null ? true : mphOver >= range.min;
    const meetsMax = range.max == null ? true : mphOver <= range.max;

    if (meetsMin && meetsMax) {
      return {
        ...row,
        isSpeeding: true,
        mphMin: range.min,
        mphMax: range.max,
      };
    }
  }

  return null;
}

// -------------------------
// Vacation House Check logic
// -------------------------
function getVacationChecks() {
  try {
    const raw = localStorage.getItem(VACATION_CHECKS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveVacationChecks(checks) {
  localStorage.setItem(VACATION_CHECKS_STORAGE_KEY, JSON.stringify(checks));
}

function generateCodeName(existingChecks = []) {
  const words = [
    "ALPHA",
    "BRAVO",
    "CHARLIE",
    "DELTA",
    "ECHO",
    "FALCON",
    "RANGER",
    "SIERRA",
    "TANGO",
    "VECTOR",
    "PATROL",
    "GUARD",
  ];

  let codeName = "";
  let isUnique = false;

  while (!isUnique) {
    const word = words[Math.floor(Math.random() * words.length)];
    const num = Math.floor(10 + Math.random() * 90);
    codeName = `${word}-${num}`;
    isUnique = !existingChecks.some((check) => check.codeName === codeName);
  }

  return codeName;
}

function createVacationCheck(data, existingChecks) {
  const returnDateObj = new Date(`${data.returnDate}T23:59:59`);
  const expiresAt = new Date(returnDateObj);
  expiresAt.setDate(expiresAt.getDate() + 1);

  return {
    id: `vhc_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    codeName: generateCodeName(existingChecks),
    residentName: data.residentName,
    phone: data.phone,
    address: data.address,
    city: data.city,
    returnDate: data.returnDate,
    expiresAt: expiresAt.toISOString(),
    createdAt: new Date().toISOString(),
    submittedBy: data.submittedBy,
  };
}

function purgeExpiredVacationChecks(checks) {
  const now = new Date();
  return checks.filter((check) => new Date(check.expiresAt) > now);
}

// -------------------------
// Red Tagged logic
// -------------------------
function getRedTaggedVehicles() {
  try {
    const raw = localStorage.getItem(RED_TAGGED_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRedTaggedVehicles(entries) {
  localStorage.setItem(RED_TAGGED_STORAGE_KEY, JSON.stringify(entries));
}

function createRedTaggedEntry(data) {
  return {
    id: `redtag_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    dateTagged: data.dateTagged,
    make: data.make,
    model: data.model,
    color: data.color,
    location: data.location,
    caseNumber: data.caseNumber,
    submittedBy: data.submittedBy,
    createdAt: new Date().toISOString(),
  };
}

// -------------------------
// Pass On logic
// -------------------------
function getPassOns() {
  try {
    const raw = localStorage.getItem(PASS_ONS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function savePassOns(entries) {
  localStorage.setItem(PASS_ONS_STORAGE_KEY, JSON.stringify(entries));
}

function purgeExpiredPassOns(entries) {
  const now = new Date();
  return entries.filter((entry) => {
    if (!entry.expirationDate) return true;
    const dt = new Date(`${entry.expirationDate}T23:59:59`);
    return dt > now;
  });
}

function createPassOnEntry(data) {
  return {
    id: `passon_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    submissionType: data.submissionType,
    title: data.title,
    details: data.details,
    priority: data.priority,
    relatedPerson: data.relatedPerson,
    relatedVehicle: data.relatedVehicle,
    location: data.location,
    caseNumber: data.caseNumber,
    expirationDate: data.expirationDate,
    submittedBy: data.submittedBy,
    createdAt: new Date().toISOString(),
  };
}

// -------------------------
// Auth Screen
// -------------------------
function AuthScreen({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
  });
  const [registerData, setRegisterData] = useState({
    displayName: "",
    username: "",
    password: "",
    confirmPassword: "",
  });

  function handleLoginSubmit(e) {
    e.preventDefault();

    const username = normalizeUsername(loginData.username);
    const password = loginData.password;

    if (!username || !password) {
      alert("Please enter username and password.");
      return;
    }

    const users = getStoredUsers();
    const match = users.find(
      (u) => normalizeUsername(u.username) === username && u.password === password
    );

    if (!match) {
      alert("Invalid username or password.");
      return;
    }

    if (match.isDisabled) {
      alert("This account has been disabled.");
      return;
    }

    const sessionUser = {
      id: match.id,
      username: match.username,
      displayName: match.displayName,
      role: match.role || "Deputy",
    };

    saveCurrentUser(sessionUser);
    onLogin(sessionUser);
  }

  function handleRegisterSubmit(e) {
    e.preventDefault();

    const displayName = registerData.displayName.trim();
    const username = normalizeUsername(registerData.username);
    const password = registerData.password;
    const confirmPassword = registerData.confirmPassword;

    if (!displayName || !username || !password || !confirmPassword) {
      alert("Please complete all registration fields.");
      return;
    }

    if (password !== confirmPassword) {
      alert("Passwords do not match.");
      return;
    }

    const users = getStoredUsers();
    const existing = users.find(
      (u) => normalizeUsername(u.username) === username
    );

    if (existing) {
      alert("That username already exists.");
      return;
    }

    const isFirstUser = users.length === 0;

    const newUser = {
      id: `user_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      displayName,
      username,
      password,
      role: isFirstUser ? "Admin" : "Deputy",
      isDisabled: false,
      createdAt: new Date().toISOString(),
    };

    const updatedUsers = [...users, newUser];
    saveStoredUsers(updatedUsers);

    const sessionUser = {
      id: newUser.id,
      username: newUser.username,
      displayName: newUser.displayName,
      role: newUser.role,
    };

    saveCurrentUser(sessionUser);
    onLogin(sessionUser);

    if (isFirstUser) {
      alert("First account created. This account has been assigned Admin.");
    } else {
      alert("Account created. New users default to Deputy until changed by an Admin.");
    }
  }

  const existingUsers = getStoredUsers();
  const firstUserExists = existingUsers.length > 0;

  return (
    <div style={styles.authWrapper}>
      <div style={styles.authCard}>
        <h1 style={styles.authTitle}>Patrol Operations App</h1>
        <p style={styles.authSubtitle}>
          Sign in or create an account to continue.
        </p>

        <div style={styles.authToggleRow}>
          <button
            style={{
              ...styles.authToggleButton,
              ...(mode === "login" ? styles.authToggleActive : {}),
            }}
            onClick={() => setMode("login")}
            type="button"
          >
            Login
          </button>
          <button
            style={{
              ...styles.authToggleButton,
              ...(mode === "register" ? styles.authToggleActive : {}),
            }}
            onClick={() => setMode("register")}
            type="button"
          >
            Register
          </button>
        </div>

        {mode === "login" ? (
          <form onSubmit={handleLoginSubmit} style={styles.form}>
            <div>
              <label style={styles.label}>Username</label>
              <input
                style={styles.input}
                type="text"
                value={loginData.username}
                onChange={(e) =>
                  setLoginData((prev) => ({ ...prev, username: e.target.value }))
                }
                required
              />
            </div>

            <div>
              <label style={styles.label}>Password</label>
              <input
                style={styles.input}
                type="password"
                value={loginData.password}
                onChange={(e) =>
                  setLoginData((prev) => ({ ...prev, password: e.target.value }))
                }
                required
              />
            </div>

            <button type="submit" style={styles.primaryButton}>
              Login
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegisterSubmit} style={styles.form}>
            <div>
              <label style={styles.label}>Display Name</label>
              <input
                style={styles.input}
                type="text"
                value={registerData.displayName}
                onChange={(e) =>
                  setRegisterData((prev) => ({
                    ...prev,
                    displayName: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div>
              <label style={styles.label}>Username</label>
              <input
                style={styles.input}
                type="text"
                value={registerData.username}
                onChange={(e) =>
                  setRegisterData((prev) => ({
                    ...prev,
                    username: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div style={styles.sectionCard}>
              <p style={styles.smallText}>
                {firstUserExists
                  ? "New users are created as Deputy by default. Admins can change roles later."
                  : "The first account created will become Admin automatically."}
              </p>
            </div>

            <div>
              <label style={styles.label}>Password</label>
              <input
                style={styles.input}
                type="password"
                value={registerData.password}
                onChange={(e) =>
                  setRegisterData((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div>
              <label style={styles.label}>Confirm Password</label>
              <input
                style={styles.input}
                type="password"
                value={registerData.confirmPassword}
                onChange={(e) =>
                  setRegisterData((prev) => ({
                    ...prev,
                    confirmPassword: e.target.value,
                  }))
                }
                required
              />
            </div>

            <button type="submit" style={styles.successButton}>
              Register
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// -------------------------
// Traffic Module Component
// -------------------------
function TrafficModule() {
  const [jurisdiction, setJurisdiction] = useState("COUNTY_SAGUACHE");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [courtChoice, setCourtChoice] = useState("");

  const [mphOver, setMphOver] = useState("");
  const [stateMax75, setStateMax75] = useState(false);
  const [quickSpeedingMode, setQuickSpeedingMode] = useState(false);

  useEffect(() => {
    if (!ALLOWED.has(jurisdiction)) {
      setJurisdiction("COUNTY_SAGUACHE");
    }
  }, [jurisdiction]);

  const rawList = useMemo(() => {
    if (jurisdiction === "MUNI_SAGUACHE") return Array.isArray(muni) ? muni : [];
    if (jurisdiction === "COUNTY_SAGUACHE") return Array.isArray(county) ? county : [];
    if (jurisdiction === "STATE_CO") return Array.isArray(state) ? state : [];
    return [];
  }, [jurisdiction]);

  const dataset = useMemo(() => {
    const list = rawList;

    if (jurisdiction === "STATE_CO") {
      return list.filter((x) => String(x?.citation || "").trim().startsWith("42-"));
    }

    return list.filter((x) => String(x?.citation || "").trim().toUpperCase().startsWith("MTC"));
  }, [rawList, jurisdiction]);

  useEffect(() => {
    setSelected(null);
    setCourtChoice("");
    setQuery("");
    setMphOver("");
    setStateMax75(false);
    setQuickSpeedingMode(false);
  }, [jurisdiction]);

  const fuse = useMemo(() => {
    return new Fuse(dataset, {
      includeScore: true,
      shouldSort: true,
      ignoreLocation: true,
      threshold: 0.32,
      minMatchCharLength: 2,
      keys: [
        { name: "citation", weight: 4 },
        { name: "commonCode", weight: 3 },
        { name: "description", weight: 2 },
      ],
    });
  }, [dataset]);

  const results = useMemo(() => {
    if (quickSpeedingMode) {
      return dataset.filter(looksLikeSpeeding).slice(0, 20);
    }

    const q = query.trim();
    if (!q) return dataset.slice(0, 30);

    const strictHits = strictFilter(dataset, q);
    if (strictHits.length > 0) return strictHits.slice(0, 50);

    return fuse.search(q).slice(0, 50).map((r) => r.item);
  }, [query, dataset, fuse, quickSpeedingMode]);

  const mphNum = useMemo(() => {
    if (mphOver === "") return null;
    const n = Number(mphOver);
    return Number.isFinite(n) ? n : null;
  }, [mphOver]);

  const quickSpeedingSelection = useMemo(() => {
    if (!quickSpeedingMode) return null;
    return pickSpeedingBracket(dataset, mphNum);
  }, [quickSpeedingMode, dataset, mphNum]);

  const isSelectedSpeeding = useMemo(() => {
    return looksLikeSpeeding(selected) || quickSpeedingMode;
  }, [selected, quickSpeedingMode]);

  const derivedSpeeding = useMemo(() => {
    if (quickSpeedingSelection) return quickSpeedingSelection;
    if (!isSelectedSpeeding) return null;
    return pickSpeedingBracket(dataset, mphNum);
  }, [quickSpeedingSelection, isSelectedSpeeding, dataset, mphNum]);

  const shownViolation = derivedSpeeding ?? selected;

  const courtDates = useMemo(() => {
    try {
      const raw = getCourtDates2026({
        jurisdiction,
        requiresSummons: !!shownViolation?.requiresSummons,
      });
      return normalizeCourtDates(raw);
    } catch (e) {
      console.error("getCourtDates2026 failed:", e);
      return [];
    }
  }, [jurisdiction, shownViolation?.requiresSummons]);

  const selectedKey = useMemo(() => {
    if (!selected) return "";
    return safeStr(selected.citation || selected.commonCode || selected.description || "", "");
  }, [selected]);

  return (
    <div style={styles.trafficWrapper}>
      <h2 style={styles.h2}>Traffic Module</h2>

      <div style={styles.jurisRow}>
        {JURIS.map((t) => (
          <button
            key={t.key}
            onClick={() => setJurisdiction(t.key)}
            style={{
              ...styles.filterButton,
              background: jurisdiction === t.key ? "#111" : "#fff",
              color: jurisdiction === t.key ? "#fff" : "#111",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={styles.jurisRow}>
        <button
          onClick={() => {
            setQuickSpeedingMode((v) => !v);
            setSelected(null);
            setQuery("");
          }}
          style={{
            ...styles.filterButton,
            background: quickSpeedingMode ? "#111" : "#fff",
            color: quickSpeedingMode ? "#fff" : "#111",
            fontWeight: 600,
          }}
        >
          Quick Speeding
        </button>

        {quickSpeedingMode ? (
          <div style={styles.inlineRow}>
            <span>MPH Over:</span>
            <input
              value={mphOver}
              onChange={(e) => setMphOver(e.target.value)}
              placeholder="e.g. 12"
              inputMode="numeric"
              style={styles.smallInput}
            />
            <span style={styles.tinyText}>
              {mphNum == null ? "Enter MPH over limit" : `Parsed: ${mphNum}`}
            </span>
          </div>
        ) : null}
      </div>

      {!quickSpeedingMode ? (
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search (e.g., no insurance, MTC 603, 42-4-1409)..."
          style={styles.searchInput}
        />
      ) : null}

      <div style={styles.trafficGrid}>
        <div style={styles.resultsPanel}>
          <div style={styles.panelTitle}>
            {quickSpeedingMode ? "Speeding Results" : "Results"}{" "}
            {query.trim() && !quickSpeedingMode ? (
              <span style={styles.panelSubtitle}>(showing {results.length})</span>
            ) : null}
          </div>

          {results.length === 0 ? (
            <div style={styles.emptyState}>
              {quickSpeedingMode ? "Enter MPH over to calculate speeding." : "No matches. Try a different term."}
            </div>
          ) : (
            results.map((v, i) => {
              const key = `${jurisdiction}::${safeStr(v?.citation, "")}::${safeStr(v?.commonCode, "")}::${i}`;
              const rowKey = safeStr(v?.citation || v?.commonCode || v?.description || "", "");
              const isActive =
                (!!selectedKey && rowKey && rowKey === selectedKey) ||
                (!!shownViolation && safeStr(shownViolation?.citation, "") === safeStr(v?.citation, ""));

              return (
                <div
                  key={key}
                  onClick={() => {
                    setSelected(v ?? null);
                    setQuickSpeedingMode(false);
                  }}
                  style={{
                    ...styles.resultRow,
                    background: isActive ? "#f5f5f5" : "#fff",
                  }}
                >
                  <div style={styles.resultCitation}>{safeStr(v?.citation, "—")}</div>
                  <div style={styles.resultDescription}>{safeStr(v?.description, "")}</div>
                  {v?.commonCode ? (
                    <div style={styles.resultCommonCode}>
                      Common Code: {safeStr(v.commonCode, "")}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>

        <div style={styles.detailsPanel}>
          <div style={styles.panelTitle}>
            {quickSpeedingMode ? "Calculated Charge" : "Violation Details"}
          </div>

          {shownViolation ? (
            <>
              <div style={styles.detailLine}>
                <strong>Citation:</strong> {safeStr(shownViolation.citation, "—")}
              </div>
              <div style={styles.detailLine}>
                <strong>Description:</strong> {safeStr(shownViolation.description, "—")}
              </div>

              {shownViolation.commonCode ? (
                <div style={styles.detailLine}>
                  <strong>Common Code:</strong> {safeStr(shownViolation.commonCode, "")}
                </div>
              ) : null}

              <div style={styles.moneyGrid}>
                <div>
                  <strong>Fine:</strong>{" "}
                  {shownViolation.requiresSummons ? "SUMMONS" : fmtMoney0(shownViolation.fine)}
                </div>
                <div>
                  <strong>Surcharge:</strong>{" "}
                  {shownViolation.requiresSummons ? "SUMMONS" : fmtMoney0(shownViolation.surcharge)}
                </div>
                <div>
                  <strong>Points:</strong> {shownViolation.points == null ? "—" : safeStr(shownViolation.points, "—")}
                </div>
                <div>
                  <strong>Summons:</strong> {shownViolation.requiresSummons ? "Yes" : "No"}
                </div>
              </div>

              {looksLikeSpeeding(shownViolation) ? (
                <div style={styles.speedingBox}>
                  <div style={styles.speedingTitle}>Speeding Calculator</div>
                  <div style={styles.inlineRow}>
                    <span>MPH Over:</span>
                    <input
                      value={mphOver}
                      onChange={(e) => setMphOver(e.target.value)}
                      placeholder="e.g. 12"
                      inputMode="numeric"
                      style={styles.smallInput}
                    />
                    <span style={styles.tinyText}>
                      {mphNum == null ? "Enter MPH over limit" : `Parsed: ${mphNum}`}
                    </span>
                  </div>
                </div>
              ) : null}

              <div style={{ marginTop: 14 }}>
                <div style={styles.speedingTitle}>Court Date</div>
                <select
                  value={courtChoice}
                  onChange={(e) => setCourtChoice(e.target.value)}
                  style={styles.searchInput}
                >
                  <option value="">Select…</option>
                  {courtDates.map((label, idx) => (
                    <option key={idx} value={label}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>
              {quickSpeedingMode
                ? "Enter MPH over to auto-select the correct speeding charge."
                : "Select a violation to view details."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------
// Vacation House Check Component
// -------------------------
function VacationHouseCheck({ currentUser }) {
  const [showVacationForm, setShowVacationForm] = useState(false);
  const [vacationChecks, setVacationChecks] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    residentName: "",
    phone: "",
    address: "",
    city: "",
    returnDate: "",
  });

  const canDelete = canDeleteRecords(currentUser);
  const canEdit = canEditRecords(currentUser);

  useEffect(() => {
    if (currentUser?.id) {
      markModuleSeen(currentUser.id, "vacation");
    }
  }, [currentUser]);

  useEffect(() => {
    const cleaned = purgeExpiredVacationChecks(getVacationChecks());
    saveVacationChecks(cleaned);
    setVacationChecks(cleaned);
  }, []);

  const sortedVacationChecks = useMemo(() => {
    return [...vacationChecks].sort(
      (a, b) => new Date(a.returnDate) - new Date(b.returnDate)
    );
  }, [vacationChecks]);

  function resetForm() {
    setFormData({
      residentName: "",
      phone: "",
      address: "",
      city: "",
      returnDate: "",
    });
    setEditingId(null);
    setShowVacationForm(false);
  }

  function handleFormChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleEdit(check) {
    if (!canEdit) {
      alert("Only Supervisors and Admins can edit records.");
      return;
    }

    setEditingId(check.id);
    setFormData({
      residentName: check.residentName || "",
      phone: check.phone || "",
      address: check.address || "",
      city: check.city || "",
      returnDate: check.returnDate || "",
    });
    setShowVacationForm(true);
    window.scrollTo(0, 0);
  }

  function handleVacationSubmit(e) {
    e.preventDefault();

    const residentName = formData.residentName.trim();
    const phone = formData.phone.trim();
    const address = formData.address.trim();
    const city = formData.city.trim();
    const returnDate = formData.returnDate;

    if (!residentName || !phone || !address || !city || !returnDate) {
      alert("Please complete all fields.");
      return;
    }

    const existingChecks = getVacationChecks();

    if (editingId) {
      const updatedChecks = existingChecks.map((check) => {
        if (check.id !== editingId) return check;

        const returnDateObj = new Date(`${returnDate}T23:59:59`);
        const expiresAt = new Date(returnDateObj);
        expiresAt.setDate(expiresAt.getDate() + 1);

        return {
          ...check,
          residentName,
          phone,
          address,
          city,
          returnDate,
          expiresAt: expiresAt.toISOString(),
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser?.displayName || currentUser?.username || "Unknown User",
        };
      });

      const cleaned = purgeExpiredVacationChecks(updatedChecks);
      saveVacationChecks(cleaned);
      setVacationChecks(cleaned);
      markModuleSeen(currentUser?.id, "vacation");
      resetForm();
      alert("Vacation house check updated.");
      return;
    }

    const newCheck = createVacationCheck(
      {
        residentName,
        phone,
        address,
        city,
        returnDate,
        submittedBy: currentUser?.displayName || currentUser?.username || "Unknown User",
      },
      existingChecks
    );

    const updatedChecks = purgeExpiredVacationChecks([...existingChecks, newCheck]);
    saveVacationChecks(updatedChecks);
    setVacationChecks(updatedChecks);
    markModuleSeen(currentUser?.id, "vacation");

    resetForm();
    alert(`Vacation house check created.\nCode Name: ${newCheck.codeName}`);
  }

  function handleDeleteVacationCheck(id) {
    if (!canDelete) {
      alert("Only Supervisors and Admins can delete records.");
      return;
    }

    const updatedChecks = vacationChecks.filter((check) => check.id !== id);
    saveVacationChecks(updatedChecks);
    setVacationChecks(updatedChecks);
  }

  return (
    <div>
      <div style={styles.topActions}>
        <button
          style={styles.primaryButton}
          onClick={() => {
            setEditingId(null);
            setShowVacationForm(true);
          }}
        >
          New Submission
        </button>
      </div>

      <div style={styles.sectionCard}>
        <p style={styles.smallText}>
          Permissions: Deputies can create records. Supervisors and Admins can create, edit, and delete records.
        </p>
      </div>

      {showVacationForm && (
        <div style={styles.sectionCard}>
          <h2 style={styles.h2}>
            {editingId ? "Edit Vacation House Check" : "New Vacation House Check"}
          </h2>

          <form onSubmit={handleVacationSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>Name of Resident</label>
                <input
                  style={styles.input}
                  type="text"
                  name="residentName"
                  value={formData.residentName}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div>
                <label style={styles.label}>Contact Phone Number</label>
                <input
                  style={styles.input}
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div>
                <label style={styles.label}>Address</label>
                <input
                  style={styles.input}
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div>
                <label style={styles.label}>City</label>
                <input
                  style={styles.input}
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div>
                <label style={styles.label}>Date of Resident Return</label>
                <input
                  style={styles.input}
                  type="date"
                  name="returnDate"
                  value={formData.returnDate}
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>

            <div style={styles.topActions}>
              <button type="submit" style={styles.successButton}>
                {editingId ? "Save Changes" : "Save Submission"}
              </button>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={styles.sectionCard}>
        <h2 style={styles.h2}>Active Vacation House Checks</h2>
        <p style={styles.smallText}>
          A radio-friendly code name is automatically created for each submission.
          Expired submissions are removed automatically after the resident return
          date passes.
        </p>

        <div style={styles.vacationList}>
          {sortedVacationChecks.length === 0 ? (
            <div style={styles.vacationCard}>
              <div style={styles.muted}>No active vacation house checks.</div>
            </div>
          ) : (
            sortedVacationChecks.map((check) => (
              <div key={check.id} style={styles.vacationCard}>
                <div style={styles.vacationCardHeader}>
                  <span style={styles.codeBadge}>{check.codeName}</span>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {canEdit ? (
                      <button
                        style={styles.secondaryButton}
                        onClick={() => handleEdit(check)}
                      >
                        Edit
                      </button>
                    ) : (
                      <span style={styles.permissionPill}>View Only Edit</span>
                    )}

                    {canDelete ? (
                      <button
                        style={styles.dangerButton}
                        onClick={() => handleDeleteVacationCheck(check.id)}
                      >
                        Delete
                      </button>
                    ) : (
                      <span style={styles.permissionPill}>View Only Delete</span>
                    )}
                  </div>
                </div>

                <div style={styles.detailGrid}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Resident:</span>{" "}
                    {check.residentName}
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Phone:</span> {check.phone}
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>City:</span> {check.city}
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Return Date:</span>{" "}
                    {formatDate(check.returnDate)}
                  </div>
                  <div style={{ ...styles.detailItem, gridColumn: "1 / -1" }}>
                    <span style={styles.detailLabel}>Address:</span>{" "}
                    {check.address}
                  </div>
                  <div style={{ ...styles.detailItem, gridColumn: "1 / -1" }}>
                    <span style={styles.detailLabel}>Submitted By:</span>{" "}
                    {check.submittedBy}
                  </div>
                  {check.updatedBy ? (
                    <div style={{ ...styles.detailItem, gridColumn: "1 / -1" }}>
                      <span style={styles.detailLabel}>Last Updated By:</span>{" "}
                      {check.updatedBy}
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------
// Red Tagged Component
// -------------------------
function RedTaggedModule({ currentUser }) {
  const [showForm, setShowForm] = useState(false);
  const [entries, setEntries] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    dateTagged: "",
    make: "",
    model: "",
    color: "",
    location: "",
    caseNumber: "",
  });

  const canDelete = canDeleteRecords(currentUser);
  const canEdit = canEditRecords(currentUser);

  useEffect(() => {
    if (currentUser?.id) {
      markModuleSeen(currentUser.id, "redtagged");
    }
  }, [currentUser]);

  useEffect(() => {
    setEntries(getRedTaggedVehicles());
  }, []);

  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => sortByDateDesc(a.dateTagged, b.dateTagged));
  }, [entries]);

  function resetForm() {
    setFormData({
      dateTagged: "",
      make: "",
      model: "",
      color: "",
      location: "",
      caseNumber: "",
    });
    setEditingId(null);
    setShowForm(false);
  }

  function handleFormChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleEdit(entry) {
    if (!canEdit) {
      alert("Only Supervisors and Admins can edit records.");
      return;
    }

    setEditingId(entry.id);
    setFormData({
      dateTagged: entry.dateTagged || "",
      make: entry.make || "",
      model: entry.model || "",
      color: entry.color || "",
      location: entry.location || "",
      caseNumber: entry.caseNumber || "",
    });
    setShowForm(true);
    window.scrollTo(0, 0);
  }

  function handleSubmit(e) {
    e.preventDefault();

    const dateTagged = formData.dateTagged;
    const make = formData.make.trim();
    const model = formData.model.trim();
    const color = formData.color.trim();
    const location = formData.location.trim();
    const caseNumber = formData.caseNumber.trim();

    if (!dateTagged || !make || !model || !color || !location || !caseNumber) {
      alert("Please complete all fields.");
      return;
    }

    const existing = getRedTaggedVehicles();

    if (editingId) {
      const updated = existing.map((entry) => {
        if (entry.id !== editingId) return entry;

        return {
          ...entry,
          dateTagged,
          make,
          model,
          color,
          location,
          caseNumber,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser?.displayName || currentUser?.username || "Unknown User",
        };
      });

      saveRedTaggedVehicles(updated);
      setEntries(updated);
      markModuleSeen(currentUser?.id, "redtagged");
      resetForm();
      alert("Red tagged vehicle submission updated.");
      return;
    }

    const newEntry = createRedTaggedEntry({
      dateTagged,
      make,
      model,
      color,
      location,
      caseNumber,
      submittedBy: currentUser?.displayName || currentUser?.username || "Unknown User",
    });

    const updated = [newEntry, ...existing];
    saveRedTaggedVehicles(updated);
    setEntries(updated);
    markModuleSeen(currentUser?.id, "redtagged");

    resetForm();
    alert("Red tagged vehicle submission created.");
  }

  function handleDelete(id) {
    if (!canDelete) {
      alert("Only Supervisors and Admins can delete records.");
      return;
    }

    const updated = entries.filter((entry) => entry.id !== id);
    saveRedTaggedVehicles(updated);
    setEntries(updated);
  }

  return (
    <div>
      <div style={styles.topActions}>
        <button
          style={styles.primaryButton}
          onClick={() => {
            setEditingId(null);
            setShowForm(true);
          }}
        >
          New Submission
        </button>
      </div>

      <div style={styles.sectionCard}>
        <p style={styles.smallText}>
          Permissions: Deputies can create records. Supervisors and Admins can create, edit, and delete records.
        </p>
      </div>

      {showForm && (
        <div style={styles.sectionCard}>
          <h2 style={styles.h2}>
            {editingId ? "Edit Red Tagged Vehicle" : "New Red Tagged Vehicle"}
          </h2>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>Date of Tag</label>
                <input
                  style={styles.input}
                  type="date"
                  name="dateTagged"
                  value={formData.dateTagged}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div>
                <label style={styles.label}>Vehicle Make</label>
                <input
                  style={styles.input}
                  type="text"
                  name="make"
                  value={formData.make}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div>
                <label style={styles.label}>Vehicle Model</label>
                <input
                  style={styles.input}
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div>
                <label style={styles.label}>Vehicle Color</label>
                <input
                  style={styles.input}
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div>
                <label style={styles.label}>Location of Vehicle</label>
                <input
                  style={styles.input}
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div>
                <label style={styles.label}>Case Number</label>
                <input
                  style={styles.input}
                  type="text"
                  name="caseNumber"
                  value={formData.caseNumber}
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>

            <div style={styles.topActions}>
              <button type="submit" style={styles.successButton}>
                {editingId ? "Save Changes" : "Save Submission"}
              </button>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={styles.sectionCard}>
        <h2 style={styles.h2}>Red Tagged Vehicles</h2>
        <p style={styles.smallText}>
          Each submission is stamped with the logged-in user.
        </p>

        <div style={styles.vacationList}>
          {sortedEntries.length === 0 ? (
            <div style={styles.vacationCard}>
              <div style={styles.muted}>No red tagged vehicle submissions.</div>
            </div>
          ) : (
            sortedEntries.map((entry) => (
              <div key={entry.id} style={styles.vacationCard}>
                <div style={styles.vacationCardHeader}>
                  <span style={styles.codeBadge}>
                    {safeStr(entry.make)} {safeStr(entry.model)}
                  </span>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {canEdit ? (
                      <button
                        style={styles.secondaryButton}
                        onClick={() => handleEdit(entry)}
                      >
                        Edit
                      </button>
                    ) : (
                      <span style={styles.permissionPill}>View Only Edit</span>
                    )}

                    {canDelete ? (
                      <button
                        style={styles.dangerButton}
                        onClick={() => handleDelete(entry.id)}
                      >
                        Delete
                      </button>
                    ) : (
                      <span style={styles.permissionPill}>View Only Delete</span>
                    )}
                  </div>
                </div>

                <div style={styles.detailGrid}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Date of Tag:</span>{" "}
                    {formatDate(entry.dateTagged)}
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Color:</span> {entry.color}
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Case Number:</span>{" "}
                    {entry.caseNumber}
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Submitted By:</span>{" "}
                    {entry.submittedBy}
                  </div>
                  <div style={{ ...styles.detailItem, gridColumn: "1 / -1" }}>
                    <span style={styles.detailLabel}>Location:</span>{" "}
                    {entry.location}
                  </div>
                  {entry.updatedBy ? (
                    <div style={{ ...styles.detailItem, gridColumn: "1 / -1" }}>
                      <span style={styles.detailLabel}>Last Updated By:</span>{" "}
                      {entry.updatedBy}
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------
// Pass On Component
// -------------------------
function PassOnModule({ currentUser }) {
  const [showForm, setShowForm] = useState(false);
  const [entries, setEntries] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [formData, setFormData] = useState({
    submissionType: PASS_ON_TYPES[0],
    title: "",
    details: "",
    priority: "Normal",
    relatedPerson: "",
    relatedVehicle: "",
    location: "",
    caseNumber: "",
    expirationDate: "",
  });

  const canDelete = canDeleteRecords(currentUser);
  const canEdit = canEditRecords(currentUser);

  useEffect(() => {
    if (currentUser?.id) {
      markModuleSeen(currentUser.id, "passon");
    }
  }, [currentUser]);

  useEffect(() => {
    const cleaned = purgeExpiredPassOns(getPassOns());
    savePassOns(cleaned);
    setEntries(cleaned);
  }, []);

  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      const prioWeight = { High: 3, Normal: 2, Low: 1 };
      const prioDiff = (prioWeight[b.priority] || 0) - (prioWeight[a.priority] || 0);
      if (prioDiff !== 0) return prioDiff;
      return sortByDateDesc(a.createdAt, b.createdAt);
    });
  }, [entries]);

  function resetForm() {
    setFormData({
      submissionType: PASS_ON_TYPES[0],
      title: "",
      details: "",
      priority: "Normal",
      relatedPerson: "",
      relatedVehicle: "",
      location: "",
      caseNumber: "",
      expirationDate: "",
    });
    setEditingId(null);
    setExpandedId(null);
    setShowForm(false);
  }

  function handleFormChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleEdit(entry) {
    if (!canEdit) {
      alert("Only Supervisors and Admins can edit records.");
      return;
    }

    setEditingId(entry.id);
    setFormData({
      submissionType: entry.submissionType || PASS_ON_TYPES[0],
      title: entry.title || "",
      details: entry.details || "",
      priority: entry.priority || "Normal",
      relatedPerson: entry.relatedPerson || "",
      relatedVehicle: entry.relatedVehicle || "",
      location: entry.location || "",
      caseNumber: entry.caseNumber || "",
      expirationDate: entry.expirationDate || "",
    });
    setShowForm(true);
    window.scrollTo(0, 0);
  }

  function handleSubmit(e) {
    e.preventDefault();

    const submissionType = formData.submissionType;
    const title = formData.title.trim();
    const details = formData.details.trim();
    const priority = formData.priority;
    const relatedPerson = formData.relatedPerson.trim();
    const relatedVehicle = formData.relatedVehicle.trim();
    const location = formData.location.trim();
    const caseNumber = formData.caseNumber.trim();
    const expirationDate = formData.expirationDate;

    if (!submissionType || !title || !details || !priority || !expirationDate) {
      alert("Please complete all required fields.");
      return;
    }

    const existing = getPassOns();

    if (editingId) {
      const updated = existing.map((entry) => {
        if (entry.id !== editingId) return entry;

        return {
          ...entry,
          submissionType,
          title,
          details,
          priority,
          relatedPerson,
          relatedVehicle,
          location,
          caseNumber,
          expirationDate,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser?.displayName || currentUser?.username || "Unknown User",
        };
      });

      const cleaned = purgeExpiredPassOns(updated);
      savePassOns(cleaned);
      setEntries(cleaned);
      markModuleSeen(currentUser?.id, "passon");
      resetForm();
      alert("Pass on updated.");
      return;
    }

    const newEntry = createPassOnEntry({
      submissionType,
      title,
      details,
      priority,
      relatedPerson,
      relatedVehicle,
      location,
      caseNumber,
      expirationDate,
      submittedBy: currentUser?.displayName || currentUser?.username || "Unknown User",
    });

    const updated = purgeExpiredPassOns([newEntry, ...existing]);
    savePassOns(updated);
    setEntries(updated);
    markModuleSeen(currentUser?.id, "passon");

    resetForm();
    alert("Pass on created.");
  }

  function handleDelete(id) {
    if (!canDelete) {
      alert("Only Supervisors and Admins can delete records.");
      return;
    }

    const updated = entries.filter((entry) => entry.id !== id);
    savePassOns(updated);
    setEntries(updated);
  }

  function priorityStyle(priority) {
    if (priority === "High") return styles.highPriorityPill;
    if (priority === "Low") return styles.lowPriorityPill;
    return styles.normalPriorityPill;
  }

  return (
    <div>
      <div style={styles.topActions}>
        <button
          style={styles.primaryButton}
          onClick={() => {
            setEditingId(null);
            setShowForm(true);
          }}
        >
          New Submission
        </button>
      </div>

      <div style={styles.sectionCard}>
        <p style={styles.smallText}>
          Suggested types include Officer Safety, Shift Recap, ATL / BOLO, Extra Patrol Request, and Case Follow-Up.
          Deputies can create records. Supervisors and Admins can create, edit, and delete records.
        </p>
      </div>

      {showForm && (
        <div style={styles.sectionCard}>
          <h2 style={styles.h2}>
            {editingId ? "Edit Pass On" : "New Pass On"}
          </h2>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>Submission Type</label>
                <select
                  style={styles.input}
                  name="submissionType"
                  value={formData.submissionType}
                  onChange={handleFormChange}
                  required
                >
                  {PASS_ON_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={styles.label}>Priority</label>
                <select
                  style={styles.input}
                  name="priority"
                  value={formData.priority}
                  onChange={handleFormChange}
                  required
                >
                  {PASS_ON_PRIORITIES.map((priority) => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={styles.label}>Title / Short Label</label>
                <input
                  style={styles.input}
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={styles.label}>Details / Narrative</label>
                <textarea
                  style={styles.textarea}
                  name="details"
                  value={formData.details}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div>
                <label style={styles.label}>Related Person</label>
                <input
                  style={styles.input}
                  type="text"
                  name="relatedPerson"
                  value={formData.relatedPerson}
                  onChange={handleFormChange}
                />
              </div>

              <div>
                <label style={styles.label}>Related Vehicle</label>
                <input
                  style={styles.input}
                  type="text"
                  name="relatedVehicle"
                  value={formData.relatedVehicle}
                  onChange={handleFormChange}
                />
              </div>

              <div>
                <label style={styles.label}>Location</label>
                <input
                  style={styles.input}
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleFormChange}
                />
              </div>

              <div>
                <label style={styles.label}>Case Number</label>
                <input
                  style={styles.input}
                  type="text"
                  name="caseNumber"
                  value={formData.caseNumber}
                  onChange={handleFormChange}
                />
              </div>

              <div>
                <label style={styles.label}>Expiration Date</label>
                <input
                  style={styles.input}
                  type="date"
                  name="expirationDate"
                  value={formData.expirationDate}
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>

            <div style={styles.topActions}>
              <button type="submit" style={styles.successButton}>
                {editingId ? "Save Changes" : "Save Submission"}
              </button>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={styles.sectionCard}>
        <h2 style={styles.h2}>Active Pass Ons</h2>
        <p style={styles.smallText}>
          High-priority pass ons float to the top. Expired pass ons are removed automatically after the expiration date passes.
        </p>

        <div style={styles.vacationList}>
          {sortedEntries.length === 0 ? (
            <div style={styles.vacationCard}>
              <div style={styles.muted}>No active pass ons.</div>
            </div>
          ) : (
            sortedEntries.map((entry) => {
              const isExpanded = expandedId === entry.id;

              return (
                <div key={entry.id} style={styles.vacationCard}>
                  <button
                    type="button"
                    style={styles.passOnCollapseButton}
                    onClick={() =>
                      setExpandedId((prev) => (prev === entry.id ? null : entry.id))
                    }
                  >
                    <div style={styles.vacationCardHeader}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={styles.codeBadge}>{entry.submissionType}</span>
                        <span style={priorityStyle(entry.priority)}>{entry.priority}</span>
                      </div>

                      <span style={styles.passOnToggleText}>
                        {isExpanded ? "Hide Details" : "View Details"}
                      </span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 700, marginBottom: 8 }}>
                        {entry.title}
                      </div>

                      <div style={{ marginBottom: 10, whiteSpace: "pre-wrap" }}>
                        {entry.details}
                      </div>

                      <div style={styles.detailGrid}>
                        <div style={styles.detailItem}>
                          <span style={styles.detailLabel}>Related Person:</span>{" "}
                          {entry.relatedPerson || "—"}
                        </div>
                        <div style={styles.detailItem}>
                          <span style={styles.detailLabel}>Related Vehicle:</span>{" "}
                          {entry.relatedVehicle || "—"}
                        </div>
                        <div style={styles.detailItem}>
                          <span style={styles.detailLabel}>Location:</span>{" "}
                          {entry.location || "—"}
                        </div>
                        <div style={styles.detailItem}>
                          <span style={styles.detailLabel}>Case Number:</span>{" "}
                          {entry.caseNumber || "—"}
                        </div>
                        <div style={styles.detailItem}>
                          <span style={styles.detailLabel}>Expiration Date:</span>{" "}
                          {formatDate(entry.expirationDate)}
                        </div>
                        <div style={styles.detailItem}>
                          <span style={styles.detailLabel}>Submitted By:</span>{" "}
                          {entry.submittedBy}
                        </div>
                        <div style={styles.detailItem}>
                          <span style={styles.detailLabel}>Created:</span>{" "}
                          {formatDateTime(entry.createdAt)}
                        </div>
                        {entry.updatedBy ? (
                          <div style={styles.detailItem}>
                            <span style={styles.detailLabel}>Last Updated By:</span>{" "}
                            {entry.updatedBy}
                          </div>
                        ) : null}
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                        {canEdit ? (
                          <button
                            style={styles.secondaryButton}
                            onClick={() => handleEdit(entry)}
                          >
                            Edit
                          </button>
                        ) : (
                          <span style={styles.permissionPill}>View Only Edit</span>
                        )}

                        {canDelete ? (
                          <button
                            style={styles.dangerButton}
                            onClick={() => handleDelete(entry.id)}
                          >
                            Delete
                          </button>
                        ) : (
                          <span style={styles.permissionPill}>View Only Delete</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------
// Admin User Management
// -------------------------
function UserManagementModule({ currentUser, onSessionRefresh }) {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    setUsers(getStoredUsers());
  }, []);

  function refreshUsers() {
    const refreshed = getStoredUsers();
    setUsers(refreshed);
    onSessionRefresh?.();
  }

  function handleRoleChange(userId, newRole) {
    const users = getStoredUsers();
    const updated = users.map((user) => {
      if (user.id !== userId) return user;
      return { ...user, role: newRole };
    });

    saveStoredUsers(updated);
    refreshUsers();
  }

  function handleDisableToggle(userId) {
    const users = getStoredUsers();
    const target = users.find((u) => u.id === userId);
    if (!target) return;

    if (target.id === currentUser.id) {
      alert("You cannot disable your own account.");
      return;
    }

    const updated = users.map((user) => {
      if (user.id !== userId) return user;
      return { ...user, isDisabled: !user.isDisabled };
    });

    saveStoredUsers(updated);
    refreshUsers();
  }

  const sortedUsers = [...users].sort((a, b) => {
    if (a.role === "Admin" && b.role !== "Admin") return -1;
    if (a.role !== "Admin" && b.role === "Admin") return 1;
    return safeStr(a.displayName).localeCompare(safeStr(b.displayName));
  });

  return (
    <div>
      <div style={styles.sectionCard}>
        <h2 style={styles.h2}>User Management</h2>
        <p style={styles.smallText}>
          Admin-only module. Manage user roles and disable accounts.
        </p>
      </div>

      <div style={styles.sectionCard}>
        {sortedUsers.length === 0 ? (
          <div style={styles.muted}>No users found.</div>
        ) : (
          <div style={styles.userTableWrapper}>
            <table style={styles.userTable}>
              <thead>
                <tr>
                  <th style={styles.userTableTh}>Display Name</th>
                  <th style={styles.userTableTh}>Username</th>
                  <th style={styles.userTableTh}>Role</th>
                  <th style={styles.userTableTh}>Status</th>
                  <th style={styles.userTableTh}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => {
                  const isSelf = user.id === currentUser.id;
                  const isDisabled = !!user.isDisabled;

                  return (
                    <tr key={user.id}>
                      <td style={styles.userTableTd}>{user.displayName}</td>
                      <td style={styles.userTableTd}>{user.username}</td>
                      <td style={styles.userTableTd}>
                        <select
                          style={styles.compactInput}
                          value={user.role || "Deputy"}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          disabled={isSelf}
                        >
                          {ROLES.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={styles.userTableTd}>
                        {isDisabled ? (
                          <span style={styles.disabledPill}>Disabled</span>
                        ) : (
                          <span style={styles.activePill}>Active</span>
                        )}
                      </td>
                      <td style={styles.userTableTd}>
                        {isSelf ? (
                          <span style={styles.permissionPill}>Current User</span>
                        ) : (
                          <button
                            style={isDisabled ? styles.successButton : styles.dangerButton}
                            onClick={() => handleDisableToggle(user.id)}
                          >
                            {isDisabled ? "Enable" : "Disable"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------
// App
// -------------------------
export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentScreen, setCurrentScreen] = useState("home");
  const [alertRefreshKey, setAlertRefreshKey] = useState(0);

  useEffect(() => {
    const existingSession = getCurrentUser();
    if (existingSession) {
      const synced = syncSessionWithStoredUsers(existingSession);
      setCurrentUser(synced);
    }
  }, []);

  function handleLogin(user) {
    setCurrentUser(user);
    setCurrentScreen("home");
    setAlertRefreshKey((prev) => prev + 1);
    window.scrollTo(0, 0);
  }

  function handleLogout() {
    clearCurrentUser();
    setCurrentUser(null);
    setCurrentScreen("home");
    setAlertRefreshKey((prev) => prev + 1);
  }

  function handleNavigate(screen) {
    if (currentUser?.id && ["vacation", "redtagged", "passon"].includes(screen)) {
      markModuleSeen(currentUser.id, screen);
    }

    setCurrentScreen(screen);
    setAlertRefreshKey((prev) => prev + 1);
    window.scrollTo(0, 0);
  }

  function handleSessionRefresh() {
    const refreshed = syncSessionWithStoredUsers(getCurrentUser());
    setCurrentUser(refreshed);
    setAlertRefreshKey((prev) => prev + 1);
  }

  const vacationUnreadCount = useMemo(() => {
    const cleaned = purgeExpiredVacationChecks(getVacationChecks());
    return getUnreadModuleCount(currentUser?.id, "vacation", cleaned);
  }, [currentUser, currentScreen, alertRefreshKey]);

  const redTaggedUnreadCount = useMemo(() => {
    return getUnreadModuleCount(currentUser?.id, "redtagged", getRedTaggedVehicles());
  }, [currentUser, currentScreen, alertRefreshKey]);

  const passOnUnreadCount = useMemo(() => {
    const cleaned = purgeExpiredPassOns(getPassOns());
    return getUnreadModuleCount(currentUser?.id, "passon", cleaned);
  }, [currentUser, currentScreen, alertRefreshKey]);

  if (!currentUser) {
    return <AuthScreen onLogin={handleLogin} />;
  }

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div>
            <div style={styles.headerTitle}>Patrol Operations App</div>
            <div style={styles.headerUser}>
              Logged in as: {currentUser.displayName} ({currentUser.username}) • {currentUser.role}
            </div>
          </div>

          <button style={styles.secondaryButton} onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <main style={styles.main}>
        {currentScreen === "home" && (
          <section style={styles.sectionCard}>
            <h2 style={styles.h2}>Dashboard</h2>
            <p style={styles.muted}>Select a module.</p>

            <div style={styles.sectionCard}>
              <p style={styles.smallText}>
                Current permissions for <strong>{currentUser.role}</strong>: create records in supported modules
                {canDeleteRecords(currentUser) ? " and delete records." : ", but cannot delete records."}
                {canEditRecords(currentUser) ? " Can also edit records." : " Cannot edit records."}
              </p>
            </div>

            <div style={styles.moduleGrid}>
              <div style={styles.moduleCard}>
                <h3 style={styles.h3}>Traffic Module</h3>
                <p style={styles.cardText}>
                  Citations, statutes, court date tools, and traffic-related functions.
                </p>
                <button
                  style={styles.primaryButton}
                  onClick={() => handleNavigate("traffic")}
                >
                  Open Traffic Module
                </button>
              </div>

              <div style={styles.moduleCard}>
                <div style={styles.moduleCardHeader}>
                  <h3 style={styles.h3}>Vacation House Check</h3>
                  {vacationUnreadCount > 0 ? (
                    <span style={styles.alertBadge}>{vacationUnreadCount}</span>
                  ) : null}
                </div>
                <p style={styles.cardText}>
                  Track requested patrol checks for residences while community members are away.
                </p>
                <button
                  style={styles.primaryButton}
                  onClick={() => handleNavigate("vacation")}
                >
                  Open Vacation House Check
                </button>
              </div>

              <div style={styles.moduleCard}>
                <div style={styles.moduleCardHeader}>
                  <h3 style={styles.h3}>Red Tagged</h3>
                  {redTaggedUnreadCount > 0 ? (
                    <span style={styles.alertBadge}>{redTaggedUnreadCount}</span>
                  ) : null}
                </div>
                <p style={styles.cardText}>
                  Track vehicles that have been red tagged, along with case number,
                  location, and submitting personnel.
                </p>
                <button
                  style={styles.primaryButton}
                  onClick={() => handleNavigate("redtagged")}
                >
                  Open Red Tagged
                </button>
              </div>

              <div style={styles.moduleCard}>
                <div style={styles.moduleCardHeader}>
                  <h3 style={styles.h3}>Pass On</h3>
                  {passOnUnreadCount > 0 ? (
                    <span style={styles.alertTextBadge}>New Pass On Alert</span>
                  ) : null}
                </div>
                <p style={styles.cardText}>
                  Shift-to-shift operational notes such as officer safety, BOLOs, extra patrol requests, and follow-up items.
                </p>
                <button
                  style={styles.primaryButton}
                  onClick={() => handleNavigate("passon")}
                >
                  Open Pass On
                </button>
              </div>

              {isAdmin(currentUser) && (
                <div style={styles.moduleCard}>
                  <h3 style={styles.h3}>User Management</h3>
                  <p style={styles.cardText}>
                    Admin-only control panel for roles and account status.
                  </p>
                  <button
                    style={styles.primaryButton}
                    onClick={() => handleNavigate("users")}
                  >
                    Open User Management
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {currentScreen === "traffic" && (
          <section>
            <div style={styles.topActions}>
              <button
                style={styles.secondaryButton}
                onClick={() => handleNavigate("home")}
              >
                ← Back to Home
              </button>
            </div>

            <div style={styles.sectionCard}>
              <TrafficModule />
            </div>
          </section>
        )}

        {currentScreen === "vacation" && (
          <section>
            <div style={styles.topActions}>
              <button
                style={styles.secondaryButton}
                onClick={() => handleNavigate("home")}
              >
                ← Back to Home
              </button>
            </div>

            <VacationHouseCheck currentUser={currentUser} />
          </section>
        )}

        {currentScreen === "redtagged" && (
          <section>
            <div style={styles.topActions}>
              <button
                style={styles.secondaryButton}
                onClick={() => handleNavigate("home")}
              >
                ← Back to Home
              </button>
            </div>

            <RedTaggedModule currentUser={currentUser} />
          </section>
        )}

        {currentScreen === "passon" && (
          <section>
            <div style={styles.topActions}>
              <button
                style={styles.secondaryButton}
                onClick={() => handleNavigate("home")}
              >
                ← Back to Home
              </button>
            </div>

            <PassOnModule currentUser={currentUser} />
          </section>
        )}

        {currentScreen === "users" && isAdmin(currentUser) && (
          <section>
            <div style={styles.topActions}>
              <button
                style={styles.secondaryButton}
                onClick={() => handleNavigate("home")}
              >
                ← Back to Home
              </button>
            </div>

            <UserManagementModule
              currentUser={currentUser}
              onSessionRefresh={handleSessionRefresh}
            />
          </section>
        )}
      </main>
    </div>
  );
}

const styles = {
  app: {
    minHeight: "100vh",
    background: "#f3f4f6",
    color: "#111827",
    fontFamily: "Arial, sans-serif",
  },
  header: {
    background: "#16213e",
    color: "#ffffff",
    padding: "18px 20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  },
  headerInner: {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
  },
  headerTitle: {
    fontSize: "1.4rem",
    fontWeight: "bold",
  },
  headerUser: {
    fontSize: "0.9rem",
    opacity: 0.9,
    marginTop: "4px",
  },
  main: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "18px",
  },
  sectionCard: {
    background: "#ffffff",
    borderRadius: "12px",
    padding: "18px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    marginBottom: "18px",
  },
  h2: {
    marginTop: 0,
    marginBottom: "8px",
  },
  h3: {
    marginTop: 0,
    marginBottom: "10px",
  },
  muted: {
    color: "#6b7280",
  },
  moduleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
    marginTop: "16px",
  },
  moduleCard: {
    background: "#ffffff",
    borderRadius: "12px",
    padding: "18px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    border: "1px solid #e5e7eb",
  },
  moduleCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
    flexWrap: "wrap",
  },
  alertBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "28px",
    height: "28px",
    padding: "0 8px",
    borderRadius: "999px",
    background: "#dc2626",
    color: "#ffffff",
    fontSize: "0.8rem",
    fontWeight: 700,
    boxShadow: "0 2px 8px rgba(220,38,38,0.25)",
  },
  alertTextBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 12px",
    borderRadius: "999px",
    background: "#dc2626",
    color: "#ffffff",
    fontSize: "0.8rem",
    fontWeight: 700,
    boxShadow: "0 2px 8px rgba(220,38,38,0.25)",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  cardText: {
    marginBottom: "16px",
    color: "#4b5563",
  },
  topActions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "16px",
  },
  primaryButton: {
    border: "none",
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "0.95rem",
    cursor: "pointer",
    background: "#16213e",
    color: "#ffffff",
  },
  secondaryButton: {
    border: "none",
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "0.95rem",
    cursor: "pointer",
    background: "#6b7280",
    color: "#ffffff",
  },
  successButton: {
    border: "none",
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "0.95rem",
    cursor: "pointer",
    background: "#065f46",
    color: "#ffffff",
  },
  dangerButton: {
    border: "none",
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "0.95rem",
    cursor: "pointer",
    background: "#b91c1c",
    color: "#ffffff",
  },
  permissionPill: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#e5e7eb",
    color: "#374151",
    fontSize: "0.8rem",
    fontWeight: 600,
  },
  activePill: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#dcfce7",
    color: "#166534",
    fontSize: "0.8rem",
    fontWeight: 700,
  },
  disabledPill: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#fee2e2",
    color: "#991b1b",
    fontSize: "0.8rem",
    fontWeight: 700,
  },
  highPriorityPill: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#fee2e2",
    color: "#991b1b",
    fontSize: "0.8rem",
    fontWeight: 700,
  },
  normalPriorityPill: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#dbeafe",
    color: "#1d4ed8",
    fontSize: "0.8rem",
    fontWeight: 700,
  },
  lowPriorityPill: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: "999px",
    background: "#dcfce7",
    color: "#166534",
    fontSize: "0.8rem",
    fontWeight: 700,
  },
  form: {
    display: "grid",
    gap: "14px",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "14px",
  },
  label: {
    display: "block",
    fontWeight: "bold",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "10px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    fontSize: "0.95rem",
  },
  textarea: {
    width: "100%",
    minHeight: "120px",
    padding: "10px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    fontSize: "0.95rem",
    fontFamily: "Arial, sans-serif",
    resize: "vertical",
  },
  compactInput: {
    width: "100%",
    minWidth: "120px",
    padding: "8px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    fontSize: "0.9rem",
  },
  vacationList: {
    display: "grid",
    gap: "12px",
  },
  vacationCard: {
    background: "#ffffff",
    border: "1px solid #dbe1e8",
    borderRadius: "12px",
    padding: "14px",
    boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
  },
  vacationCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "10px",
  },
  codeBadge: {
    display: "inline-block",
    background: "#16213e",
    color: "#ffffff",
    padding: "6px 10px",
    borderRadius: "999px",
    fontWeight: "bold",
    letterSpacing: "0.5px",
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "8px 14px",
    marginTop: "8px",
  },
  detailItem: {
    fontSize: "0.95rem",
  },
  detailLabel: {
    fontWeight: "bold",
    color: "#374151",
  },
  smallText: {
    fontSize: "0.9rem",
    color: "#6b7280",
  },

  passOnCollapseButton: {
    width: "100%",
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    textAlign: "left",
  },
  passOnToggleText: {
    fontSize: "0.85rem",
    fontWeight: 700,
    color: "#4b5563",
  },

  authWrapper: {
    minHeight: "100vh",
    background: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  },
  authCard: {
    width: "100%",
    maxWidth: "460px",
    background: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
  },
  authTitle: {
    marginTop: 0,
    marginBottom: "8px",
    fontSize: "1.7rem",
    color: "#16213e",
  },
  authSubtitle: {
    marginTop: 0,
    marginBottom: "18px",
    color: "#6b7280",
  },
  authToggleRow: {
    display: "flex",
    gap: "10px",
    marginBottom: "18px",
  },
  authToggleButton: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 600,
  },
  authToggleActive: {
    background: "#16213e",
    color: "#fff",
    border: "1px solid #16213e",
  },

  userTableWrapper: {
    overflowX: "auto",
  },
  userTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  userTableTh: {
    textAlign: "left",
    padding: "10px",
    borderBottom: "1px solid #e5e7eb",
    fontSize: "0.9rem",
  },
  userTableTd: {
    padding: "10px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
  },

  trafficWrapper: {
    maxWidth: "980px",
    margin: "0 auto",
    padding: 0,
    fontFamily: "system-ui, sans-serif",
  },
  jurisRow: {
    display: "flex",
    gap: 8,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  filterButton: {
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid #ccc",
    cursor: "pointer",
  },
  inlineRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  },
  smallInput: {
    width: 110,
    padding: 10,
    borderRadius: 10,
    border: "1px solid #ccc",
  },
  tinyText: {
    fontSize: 12,
    opacity: 0.7,
  },
  searchInput: {
    width: "100%",
    padding: 12,
    borderRadius: 10,
    border: "1px solid #ccc",
    marginBottom: 12,
  },
  trafficGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  resultsPanel: {
    border: "1px solid #ddd",
    borderRadius: 12,
    padding: 12,
    minHeight: 420,
  },
  detailsPanel: {
    border: "1px solid #ddd",
    borderRadius: 12,
    padding: 12,
  },
  panelTitle: {
    fontWeight: 700,
    marginBottom: 8,
  },
  panelSubtitle: {
    fontWeight: 400,
    opacity: 0.7,
  },
  emptyState: {
    opacity: 0.75,
  },
  resultRow: {
    padding: 8,
    borderBottom: "1px solid #eee",
    cursor: "pointer",
  },
  resultCitation: {
    fontWeight: 600,
  },
  resultDescription: {
    fontSize: 13,
  },
  resultCommonCode: {
    fontSize: 12,
    opacity: 0.75,
  },
  detailLine: {
    marginBottom: 6,
  },
  moneyGrid: {
    marginTop: 10,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
  },
  speedingBox: {
    marginTop: 14,
    padding: 12,
    border: "1px solid #ddd",
    borderRadius: 10,
  },
  speedingTitle: {
    fontWeight: 700,
    marginBottom: 8,
  },
};